// Story 6-5: Sync Failure Notifier Composable
// Watches for sync failures and shows notifications to admin users

import { watch, onUnmounted, ref } from 'vue'
import { useSyncStore } from '@/stores/useSyncStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useUiStore } from '@/stores/useUiStore'
import { useQuoteStore } from '@/stores/useQuoteStore'

export function useSyncFailureNotifier() {
  const syncStore = useSyncStore()
  const authStore = useAuthStore()
  const uiStore = useUiStore()
  const quoteStore = useQuoteStore()

  // Track previous failed count to detect new failures
  const previousFailedCount = ref(syncStore.failedCount)

  // Watch for new sync failures
  const stopWatcher = watch(
    () => syncStore.failedCount,
    (newCount, oldCount) => {
      // Only notify on increase (new failure)
      if (newCount <= oldCount) {
        previousFailedCount.value = newCount
        return
      }

      // Only notify admin users
      if (!authStore.isAdmin) {
        previousFailedCount.value = newCount
        return
      }

      // Find the most recent failed item
      const failedItems = syncStore.syncQueue.filter(item => item.status === 'failed')
      const latestFailed = failedItems[failedItems.length - 1]

      if (latestFailed) {
        // Mark the quote as sync failed
        quoteStore.markSyncFailed(latestFailed.quoteId, latestFailed.lastError)

        // Show notification toast
        showFailureNotification(latestFailed)
      }

      previousFailedCount.value = newCount
    },
    { immediate: false }
  )

  function showFailureNotification(failedItem: typeof syncStore.syncQueue[0]) {
    uiStore.showToast('error', failedItem.lastError || 'Erreur de synchronisation', 0)

    // Get the last toast and enhance it with title and actions
    const lastToast = uiStore.toasts[uiStore.toasts.length - 1]
    if (lastToast) {
      lastToast.title = 'Echec synchronisation'
      lastToast.actions = [
        {
          label: 'Reessayer',
          handler: () => {
            syncStore.retry(failedItem.id)
            uiStore.hideToast(lastToast.id)
          },
          variant: 'primary'
        },
        {
          label: 'Ignorer',
          handler: () => {
            uiStore.hideToast(lastToast.id)
          },
          variant: 'secondary'
        }
      ]
    }
  }

  function cleanup() {
    stopWatcher()
  }

  // Auto cleanup on unmount
  onUnmounted(cleanup)

  return {
    cleanup
  }
}

export default useSyncFailureNotifier
